import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import _ from "lodash";
import useToggle from "../../Hooks/useToggle";
import navigationContext from "../../contexts/navigationsContext";
import { navigations } from "../../Data/navigation.json";
import Drawer from "../Mobile/Drawer";
import Footer from "../Partials/Footers/HomeOne/Footer";
import HeaderOne from "../Partials/Headers/HeaderOne";
import BackToTop from "../Helpers/BackToTop";
import DrawTCPopup from "../../containers/DrawTCPopup";
import { getLotteryData } from "../../services/HomeServices";
import { addLottery } from "../../services/AuthServices";
import { getUserId, isLogin } from "../../lib/constant";
import img from "../../assets/images/dream-prize.jpeg";
import "./DrawsPage.css";

const DrawsPage = () => {
  const navigationData = navigations;
  const [drawer, setDrawer] = useToggle(false);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDraw, setSelectedDraw] = useState(undefined);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const perPage = 12;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    getData();
  }, [currentPage]);

  const getData = async () => {
    setIsLoading(true);
    try {
      const response = await getLotteryData(currentPage, perPage, getUserId());
      setData(response?.data?.data || []);
      const total = response?.data?.total || 0;
      setTotalPages(Math.ceil(total / perPage) || 1);
    } catch (error) {
      console.error("Error fetching draws:", error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuyClick = (draw) => {
    if (!isLogin()) {
      toast("Please login to participate in draw", { type: "warning" });
      navigate("/login");
      return;
    }
    setSelectedDraw(draw);
    setModalIsOpen(true);
  };

  const buyClicked = async () => {
    setSubmitLoading(true);
    try {
      let formdata = new FormData();
      formdata.append("lotteryId", selectedDraw?.id);

      await addLottery(formdata);

      setSelectedDraw(undefined);
      setModalIsOpen(false);
      toast("Successfully registered for the draw!", { type: "success" });
      navigate("/account-detail", { state: { draw: true } });
    } catch (error) {
      const message = error?.response?.data?.message || "Something went wrong";
      toast(message, { type: "error" });
    } finally {
      setSubmitLoading(false);
      setSelectedDraw(undefined);
      setModalIsOpen(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const options = { day: "2-digit", month: "short", year: "numeric" };
      return new Date(dateString).toLocaleDateString("en-IN", options);
    } catch {
      return dateString;
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Drawer
        drawer={drawer}
        action={setDrawer.toggle}
        navigationData={navigationData}
      />
      <navigationContext.Provider value={{ navigationData }}>
        <HeaderOne drawerAction={setDrawer.toggle} />
      </navigationContext.Provider>

      <section className="draws-page">
        <div className="draws-page-container">
          <div className="draws-page-header">
            <div className="draws-page-title">
              <div className="draws-page-icon">
                <i className="fas fa-gift"></i>
              </div>
              <div>
                <h1>Lucky Draws</h1>
                <p>Participate in exciting draws and win amazing prizes!</p>
              </div>
            </div>
          </div>

          <div className="draws-page-content">
            {isLoading ? (
              <div className="draws-loading">
                <div className="draws-spinner"></div>
              </div>
            ) : !_.isEmpty(data) ? (
              <>
                <div className="draws-grid">
                  {data.map((draw, i) => (
                    <div className="draws-card" key={draw?.id || i}>
                      <div className="draws-card-image">
                        <img
                          src={draw?.image || img}
                          alt={draw?.name || "Draw"}
                          loading="lazy"
                          onError={(e) => { e.target.src = img; }}
                        />
                        <span className="draws-card-badge">
                          <i className="far fa-star"></i> Featured
                        </span>
                      </div>

                      <div className="draws-card-content">
                        <h3 className="draws-card-name">{draw?.name || "Draw"}</h3>
                        <p className="draws-card-description">
                          {draw?.lottery_detail || "No description available"}
                        </p>

                        <div className="draws-card-details">
                          <div className="draws-card-detail-row">
                            <span className="draws-card-detail-label">
                              <i className="far fa-calendar-alt"></i> Start Date
                            </span>
                            <span className="draws-card-detail-value">
                              {formatDate(draw?.start_date)}
                            </span>
                          </div>
                          <div className="draws-card-detail-row">
                            <span className="draws-card-detail-label">
                              <i className="far fa-calendar-check"></i> End Date
                            </span>
                            <span className="draws-card-detail-value">
                              {formatDate(draw?.expire_date)}
                            </span>
                          </div>
                          <div className="draws-card-detail-row">
                            <span className="draws-card-detail-label">
                              <i className="far fa-users"></i> Participants
                            </span>
                            <span className="draws-card-detail-value highlight">
                              {draw?.number_of_participate || 0}
                            </span>
                          </div>
                        </div>

                        <div className="draws-card-price-section">
                          <div className="draws-card-price">
                            <span className="draws-card-price-label">Entry Fee</span>
                            <span className="draws-card-price-value">
                              <i className="fas fa-coins"></i> {draw?.buy_in || 0} Coins
                            </span>
                          </div>
                          <button
                            className="draws-card-btn"
                            disabled={submitLoading}
                            onClick={() => handleBuyClick(draw)}
                          >
                            <i className="far fa-ticket-alt"></i> Enter Draw
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="draws-pagination">
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        className={`pagination-btn ${currentPage === index + 1 ? 'active' : ''}`}
                        onClick={() => handlePageChange(index + 1)}
                      >
                        {index + 1}
                      </button>
                    ))}
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="draws-empty-state">
                <div className="draws-empty-icon">
                  <i className="fas fa-gift"></i>
                </div>
                <h3>No Draws Available</h3>
                <p>Check back later for exciting draw opportunities!</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <DrawTCPopup
        isOpen={modalIsOpen}
        onClose={() => {
          setModalIsOpen(false);
          setSelectedDraw(undefined);
        }}
        onAccept={buyClicked}
        drawName={selectedDraw?.name || ""}
        buyInAmount={selectedDraw?.buy_in || 0}
        isLoading={submitLoading}
      />

      <Footer />
      <BackToTop />
    </motion.div>
  );
};

export default DrawsPage;
